import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const EXIT_POPUP_KEY = 'zpiritualz_exit_popup_seen';
const DISCOUNT_CODE = 'LAATSTE10';
// Minimum time on page before exit-intent can trigger (30 seconds)
const MIN_TIME_ON_PAGE_MS = 30000;

export const ExitIntentPopup = () => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [readyToTrigger, setReadyToTrigger] = useState(false);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      localStorage.setItem(EXIT_POPUP_KEY, 'true');
    }, 400);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  // Only allow triggering after minimum time on page
  useEffect(() => {
    const alreadySeen = localStorage.getItem(EXIT_POPUP_KEY);
    if (alreadySeen) return;

    const timer = setTimeout(() => {
      setReadyToTrigger(true);
    }, MIN_TIME_ON_PAGE_MS);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const alreadySeen = localStorage.getItem(EXIT_POPUP_KEY);
    if (alreadySeen || !readyToTrigger) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (hasTriggered) return;
      // Only trigger when mouse leaves through the TOP of the viewport
      // relatedTarget null = mouse left the browser window
      // clientY <= 5 = very close to top (address bar / tabs area)
      if (e.relatedTarget === null && e.clientY <= 5) {
        setHasTriggered(true);
        setVisible(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasTriggered, readyToTrigger]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9998] flex items-center justify-center px-4 transition-all duration-400 ${
        closing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ background: 'rgba(5,0,16,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={handleClose}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl transition-all duration-400 ${
          closing ? 'scale-95 opacity-0 translate-y-4' : 'scale-100 opacity-100 translate-y-0'
        }`}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(145deg, #0a0018 0%, #1a0038 50%, #0a0018 100%)',
          border: '1px solid rgba(244,63,94,0.4)',
          boxShadow: '0 25px 50px -12px rgba(244,63,94,0.25), 0 0 40px rgba(244,63,94,0.1)',
        }}
      >
        {/* Top urgency bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-red-500 via-rose-500 to-red-500" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors cursor-pointer rounded-full hover:bg-white/10"
          aria-label="Sluiten"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Background glow effect */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 px-8 py-8 text-center">
          {/* Urgency icon */}
          <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-gradient-to-br from-red-500/20 to-rose-500/20 border border-red-500/30 animate-pulse">
            <i className="ri-timer-flash-line text-4xl text-rose-400" />
          </div>

          {/* Urgency headline */}
          <p className="text-rose-400 text-xs font-bold tracking-[0.25em] uppercase mb-2">
            Wacht! Nog niet weg?
          </p>
          <h2 className="text-white text-2xl md:text-3xl font-black mb-3 leading-tight">
            Je bent bijna{' '}
            <span className="text-transparent bg-gradient-to-r from-red-400 to-rose-400 bg-clip-text">
              kwijt!
            </span>
          </h2>
          <p className="text-white/60 text-sm mb-5 leading-relaxed">
            Laat deze kans niet schieten. Gebruik deze code nu en ontvang direct korting op je bestelling.
          </p>

          {/* Countdown hint */}
          <div className="flex items-center justify-center gap-2 mb-6 text-rose-400/80 text-xs font-medium">
            <i className="ri-fire-line" />
            <span>Beperkte tijd aanbieding</span>
          </div>

          {/* Discount code box */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/30 rounded-xl px-4 py-3.5 mb-6">
            <div className="flex-1 text-left">
              <p className="text-white/40 text-[10px] uppercase tracking-widest mb-0.5">Exclusieve code</p>
              <p className="text-white font-black text-xl tracking-[0.15em]">{DISCOUNT_CODE}</p>
            </div>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                copied
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-gradient-to-r from-red-500 to-rose-500 text-white hover:opacity-90'
              }`}
            >
              <i className={`text-base ${copied ? 'ri-check-line' : 'ri-file-copy-line'}`} />
              {copied ? 'Gekopieerd!' : 'Kopieer'}
            </button>
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            <Link
              to="/shop"
              onClick={handleClose}
              className="block w-full py-3.5 bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold text-sm rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap shadow-lg shadow-red-500/25"
            >
              <i className="ri-shopping-bag-3-line mr-2" />
              Nu bestellen met korting
            </Link>
            <Link
              to="/consultations"
              onClick={handleClose}
              className="block w-full py-3 border border-violet-500/40 text-violet-300 font-semibold text-sm rounded-xl hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line mr-2" />
              Of boek een gratis consult
            </Link>
          </div>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-4 mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <i className="ri-shield-check-line text-green-400" />
              <span>Veilig betalen</span>
            </div>
            <div className="flex items-center gap-1.5 text-white/30 text-xs">
              <i className="ri-truck-line text-violet-400" />
              <span>Snelle verzending</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
